import { pluralize } from '@academica-box/utils/scripts/pluralize'
import { getUiPictureData } from '@academica-box/helpers/getUiPictureData'
import type { Card, Price } from '@academica-box/components/Cards/UiProductCard/types'
import type { Images } from '@academica-box/components/Media/UiPicture/types'
import type { Tag } from '@academica-box/components/Plaques/UiTag/types'
import type { Author } from '@academica-box/components/Blocks/UiAuthor/types'
import type { AchievementCard } from '@academica-box/components/Cards/UiAchievementCard/types'
import type { InstituteTag } from '@academica-box/components/Plaques/UiInstituteTag/types'
import type { RatingLink } from '@academica-box/components/Links/UiRatingLink/types'
import { CourseTypes, type Landing, type Price as IPrice, CourseTrailerFieldsFieldsPlayerTypeEnum } from '@academica-box/swagger/academgen'
import { subscribeProMock } from '@academica-box/mocks'
import { CoursesOpenBodyAccessTypeEnum } from '@academica-box/helpers/mappingCourseData'
import { LanguageMap } from '@/constants'

export const getPrice = (price?: IPrice | null): Price => {
  return {
    old: price?.old || 0,
    current: price?.current || 0,
    currency: price?.currency || '',
    discount: price?.discount
      ? {
          tag: {
            text: `− ${price?.discount}%`
          },
          end_date: ''
        }
      : undefined
  }
}

export const getProgramTag = (coursesCounter: number): Tag => {
  return {
    icon: 'ui-nesting',
    text: `${coursesCounter} ${pluralize(coursesCounter, ['курс', 'курса', 'курсов'])}`,
  }
}

export const getInfos = (landing: Landing) => {
  const fields = landing.blocks.about_course?.fields
  const commonFields = landing.common
  const infos = []

  const computedDuration = (duration: number) => {
    const WEEKS = Math.round(duration / (24 * 7))

    if (duration >= 168) {
      return `${WEEKS} ${pluralize(WEEKS, ['неделя', 'недели', 'недель'])}`
    }

    return `${duration} ${pluralize(duration, ['час', 'часа', 'часов'])}`
  }

  const computedLevelTitle = (levelSlug: string) => {
    if (levelSlug === 'for-novichkov') {
      return 'Для начинающих'
    } else if (levelSlug === 'for_specialists') {
      return 'Для специалистов'
    }

    return 'Для всех'
  }

  commonFields.level?.slug && infos.push({
    value: computedLevelTitle(commonFields.level?.slug)
  })

  !commonFields.is_auto_opening && infos.push({
    value: 'С сертификатом',
  })

  fields?.language && infos.push({
    value: LanguageMap[fields.language] ?? '',
  })

  commonFields.duration && infos.push({
    value: computedDuration(commonFields.duration),
  })

  return infos
}

export type AccessTypeEnum = CoursesOpenBodyAccessTypeEnum
export const AccessTypeEnum = CoursesOpenBodyAccessTypeEnum

export const getCommonData = (landing: Landing) => {
  let accessType = AccessTypeEnum.Paid

  const isAutoOpening = landing.common.is_auto_opening

  if (isAutoOpening) {
    accessType = AccessTypeEnum.Free
  }

  return {
    ...landing.common,
    slug: landing.url,
    hasOtherCourses: landing.blocks.other_courses?.is_visible,
    accessType,
  }
}

type StartScreenBlock = {
  images: Images;
  provider: InstituteTag;
  rating: RatingLink;
  title: string;
  description: string;
  infos: {
    value: string;
  }[];
} | undefined;

export const getStartScreenBlock = (landing: Landing): StartScreenBlock => {
  const block = landing.blocks.about_course

  if (!block?.is_visible) {
    return
  }

  const commonFields = landing.common

  return {
    images: block.fields?.image || {},
    provider: {
      text: commonFields.company.abbreviation || '',
      images: [getUiPictureData({ sm: [commonFields.company.logo] })],
    },
    rating: {
      value: commonFields.rating || 0,
      count: commonFields.reviews_count,
      // link: '#rating', TODO включить когда заработаеют отзывы
    },
    title: block.fields?.title || '',
    description: block.fields?.description || '',
    infos: getInfos(landing),
  }
}

type CourseCardBlock = {
  card: {
    images: Images;
    plaques: {
      tag?: Tag;
      tagBgColor?: string;
      tagTextColor?: string;
      tagFontWeight?: number;
      tagTooltipContent?: {
        title?: string;
        text?: string;
      };
    }[];
    infos: {
      value: string;
    }[];
    price: Price;
    program?: Tag;
  };
} | undefined;

export const getCourseCardBlock = (landing: Landing, params?: { isSubscriptionAvailable?: boolean }): CourseCardBlock => {
  const block = landing.blocks.about_course

  if (!block?.is_visible) {
    return
  }

  const commonFields = landing.common
  const coursesCounter = landing.blocks.course_content?.fields?.courses_counter || 0
  const isVendorPack = commonFields.course_type === CourseTypes.VendorPack
  const hasPrograms = Boolean(isVendorPack && coursesCounter)
  const isSubscriptionAvailable = params?.isSubscriptionAvailable && commonFields.is_subscribe_available

  return {
    card: {
      images: block.fields?.image || {},
      infos: getInfos(landing),
      price: getPrice(commonFields.price),
      program: hasPrograms ? getProgramTag(coursesCounter) : undefined,
      plaques: [
        {
          tag: isSubscriptionAvailable ? subscribeProMock().card?.plaque?.tag : undefined,
          tagBgColor: isSubscriptionAvailable ? subscribeProMock().card?.plaque?.tagBgColor : undefined,
          tagTextColor: isSubscriptionAvailable ? subscribeProMock().card?.plaque?.tagTextColor : undefined,
          tagFontWeight: isSubscriptionAvailable ? subscribeProMock().card?.plaque?.tagFontWeight : undefined,
          tagTooltipContent: isSubscriptionAvailable ? subscribeProMock().card?.plaque?.tagTooltipContent : undefined
        }
      ]
    }
  }
}

type WillLearnBlock = {
  title: string;
  data: {
    description: string;
  }[];
} | undefined;

export const getWillLearnBlock = (landing: Landing): WillLearnBlock => {
  const block = landing.blocks.what_you_will_learn

  if (!block?.is_visible) {
    return
  }

  return {
    title: block.fields?.title || '',
    data: block.fields?.learning_items?.map(item => ({
      description: item.description
    })) || []
  }
}

type TrailerBlock = {
  title: string;
  videoSrc: string;
} | undefined;

export const getTrailerBlock = (landing: Landing): TrailerBlock => {
  const block = landing.blocks.course_trailer

  if (!block?.is_visible) {
    return
  }

  let videoSrc = ''

  if (block.fields?.player_type === CourseTrailerFieldsFieldsPlayerTypeEnum.Youtube) {
    videoSrc = `https://www.youtube.com/embed/${block.fields.video_id}`
  }

  if (block.fields?.player_type === CourseTrailerFieldsFieldsPlayerTypeEnum.Vk) {
    videoSrc = `https://vk.com/video_ext.php?${block.fields.video_id}&hd=2&js_api=1`
  }

  return {
    title: block.fields?.title || '',
    videoSrc,
  }
}

type ProgramsBlock = {
  title: string;
  description: string;
  cards: Card[];
} | undefined;

export const getProgramsBlock = (landing: Landing, params?: { isSubscriptionAvailable?: boolean }): ProgramsBlock => {
  const block = landing.blocks.course_package

  if (!block?.is_visible || !block?.fields || !block.fields.courses.length) {
    return
  }

  return {
    title: `Программа из ${block.fields.courses.length} ${pluralize(block.fields.courses.length || 0, ['курса', 'курсов', 'курсов'])}`,
    description: block.fields.description,
    cards: block.fields.courses.map(course => ({
      id: course.id,
      title: course.title,
      type: course.type,
      slug: course.url,
      link: `/${course.url}/`,
      duration: {
        icon: 'ui-clock',
        value: Number(course.duration) ? `${course.duration} ч` : '',
      },
      tag: {
        icon: 'ui-star-filled',
        text: course.rating && course.rating > 0 ? course.rating : 'Новинка',
      },
      images: course.cover,
      price: getPrice(course.price),
      provider: {
        images: [getUiPictureData({ sm: [course.company.logo] })],
        text: course.company.abbreviation || ''
      },
      isSubscribeAvailable: Boolean(params?.isSubscriptionAvailable && course.is_subscribe_available),
    })) || []
  }
}

type CourseContentBlock = {
  title: string;
  description: string;
  info: {
    courses?: string;
    modules?: string;
    topics?: string;
    hours?: string;
  };
  data: {
    title: string;
    description: string;
  }[];
} | undefined;

export const getCourseContentBlock = (landing: Landing): CourseContentBlock => {
  const block = landing.blocks.course_content

  if (!block?.is_visible) {
    return
  }

  const commonFields = landing.common
  const info: {
    courses?: string,
    modules?: string,
    topics?: string,
    hours?: string,
  } = {}

  if (block.fields?.courses_counter) {
    info.courses = `${block.fields.courses_counter} ${pluralize(block.fields.courses_counter, ['курс', 'курса', 'курсов'])}`
  }

  if (block.fields?.spoilers?.length) {
    info.modules = `${block.fields.spoilers.length} ${pluralize(block.fields.spoilers.length, ['модуль', 'модуля', 'модулей'])}`
  }

  if (block.fields?.courses_counter_topics) {
    info.topics = `${block.fields.courses_counter_topics} ${pluralize(block.fields.courses_counter_topics, ['тема', 'темы', 'тем'])}`
  }

  if (commonFields.duration) {
    info.hours = `${commonFields.duration} ${pluralize(commonFields.duration, ['час', 'часа', 'часов'])}`
  }

  return {
    title: block.fields?.title || '',
    description: block.fields?.description || '',
    info,
    data: block.fields?.spoilers || [],
  }
}

type AuthorsBlock = {
  title: string;
  authors: Author[];
} | undefined;

export const getAuthorsBlock = (landing: Landing): AuthorsBlock => {
  const block = landing.blocks.teachers

  if (!block?.is_visible) {
    return
  }

  return {
    title: block.fields?.title || '',
    authors: block.fields?.teachers?.map(teacher => ({
      images: teacher.image,
      name: teacher.name,
      position: teacher.job,
      description: teacher.teacher_description,
    })) || []
  }
}

type CertificateBlock = {
  data: {
    vendor: string;
    description: string;
    images: Images;
  }
} | undefined;

export const getCertificateBlock = (landing: Landing): CertificateBlock => {
  const commonFields = landing.common
  const isVendorPack = commonFields.course_type === CourseTypes.VendorPack

  if (commonFields.is_auto_opening) {
    return
  }

  return {
    data: {
      vendor: commonFields.company.abbreviation || '',
      description: `Подтвердит, что вы прошли ${isVendorPack ? 'программу' : 'курс'}, и усилит ваше портфолио или резюме.`,
      images: getUiPictureData({ sm: ['https://cdn.skillbox.pro/wbd-front/academica-static/certificates/certificate-empty.svg'] }),
    }
  }
}

type AchievementBlock = {
  title: string;
  cards: AchievementCard[];
} | undefined;

export const getAchievementBlock = (landing: Landing): AchievementBlock => {
  const block = landing.blocks.achievements

  if (!block?.is_visible) {
    return
  }

  return {
    title: block.fields?.title || '',
    cards: block.fields?.list?.map(item => ({
      images: item.image || {},
      title: item.title || '',
      description: item.description || '',
    })) || [],
  }
}

type FAQBlock = {
  title: string;
  data: {
    title: string;
    description: string;
  }[];
} | undefined;

export const getFaqBlock = (landing: Landing): FAQBlock => {
  const block = landing.blocks.faq

  if (!block?.is_visible) {
    return
  }

  return {
    title: block.fields?.title || '',
    data: block.fields?.faq?.map(item => ({
      title: item.question,
      description: item.answer,
    })) || []
  }
}

export type PaymentData = {
  data: {
    title: string;
    descriptions: string[];
    price: Price;
    program?: Tag;
    free?: Boolean;
    pro?: Boolean;
    trialPro?: Boolean;
  };
  loggedData?: {
    coloredIcon: string;
    icon: string;
    name: string;
    description?: string;
    avatar: Images;
    logged: boolean;
  };
  priceBanner: {
    images: Images;
    bgColor: string;
    description?: string;
  };
} | undefined;

export const getPaymentData = (landing: Landing): PaymentData => {
  const aboutCourseFields = landing.blocks.about_course?.fields
  const courseContentFields = landing.blocks.course_content?.fields
  const coursesCounter = courseContentFields?.courses_counter || 0
  const commonFields = landing.common
  const isVendorPack = commonFields.course_type === CourseTypes.VendorPack
  const hasPrograms = Boolean(isVendorPack && coursesCounter)
  const isFree = landing.common.is_auto_opening

  if (!aboutCourseFields) {
    return
  }

  let descriptions: string[] = [
    'Оплата в один клик картой любого банка РФ',
    'Сертификат после прохождения курса',
    'Бессрочный доступ к материалам — учитесь, когда хотите',
  ]

  if (isFree) {
    descriptions = [
      'Бесплатный курс от ведущих экспертов',
      'Актуальные знания, которые можно применить в реальной жизни',
      'Бессрочный доступ к материалам — учитесь, когда хотите',
    ]
  }

  return {
    data: {
      title: aboutCourseFields.title,
      descriptions,
      price: getPrice(commonFields.price),
      program: hasPrograms ? getProgramTag(coursesCounter) : undefined,
      free: isFree,
    },
    // TODO надо отсюда дропвнуть это
    loggedData: {
      coloredIcon: 'string',
      icon: 'string',
      name: 'string',
      description: 'string',
      avatar: {},
      logged: false,
    },
    priceBanner: {
      images: getUiPictureData({
        sm: [
          'https://cdn.skillbox.pro/wbd-front/academica-static/general/autopayment/subscribe-pro-price-banner-new@1x.avif',
          'https://cdn.skillbox.pro/wbd-front/academica-static/general/autopayment/subscribe-pro-price-banner-new@1x.webp',
          'https://cdn.skillbox.pro/wbd-front/academica-static/general/autopayment/subscribe-pro-price-banner-new@1x.png'
        ]
      }),
      bgColor: 'linear-gradient(283deg, #d6b7fd -0.8%, #c3ddfd 13.58%, rgba(243, 234, 255, 1) 54.43%, #c3ddfd 88.26%, #d6b7fd 105.25%)',
    },
  }
}

export const getAll = (landing: Landing, params?: { isSubscriptionAvailable?: boolean }) => ({
  commonData: getCommonData(landing),
  startScreenBlock: getStartScreenBlock(landing),
  courseCardBlock: getCourseCardBlock(landing, params),
  willLearnBlock: getWillLearnBlock(landing),
  trailerBlock: getTrailerBlock(landing),
  programsBlock: getProgramsBlock(landing, params),
  courseContentBlock: getCourseContentBlock(landing),
  authorsBlock: getAuthorsBlock(landing),
  certificateBlock: getCertificateBlock(landing),
  achievementBlock: getAchievementBlock(landing),
  faqBlock: getFaqBlock(landing),
  paymentData: getPaymentData(landing),
  rawData: landing,
})
